import { isArray } from '@/lib/util';
import clsx from 'clsx';
import './Image.scss';

type ImageProps = {
  alt?: string;
  className?: string;
  height?: number | 'auto';
  loading?: 'eager' | 'lazy';
  src?: string | string[];
  title?: string;
  width?: number | 'auto';
};

const Image = (props: ImageProps) => {
  const getImageType = (image: string): string => {
    switch (true) {
      case image.endsWith('.avif'):
        return 'image/avif';

      case image.endsWith('.jpg'):
      case image.endsWith('.jpe'):
      case image.endsWith('.jpeg'):
        return 'image/jpeg';

      case image.endsWith('.gif'):
        return 'image/gif';

      case image.endsWith('.png'):
        return 'image/png';

      case image.endsWith('.webp'):
        return 'image/webp';

      default:
        throw new Error(`Unsupported image type: ${image}`);
    }
  };

  let avifImages: string[] | undefined, webpImages: string[] | undefined, otherImages: string[] | undefined;

  if (isArray(props.src)) {
    const srcArray = props.src as string[];
    // Prioritize AVIF and WebP images
    avifImages = srcArray.filter((image: string) => image.endsWith('.avif'));
    webpImages = srcArray.filter((image: string) => image.endsWith('.webp'));

    // All other valid image types
    const otherValidImageExtensions = [ '.jpg', '.jpe', '.jpeg', '.gif', '.png' ];

    otherImages = srcArray.filter((image: string) => {
      return otherValidImageExtensions.some((suffix) => {
        return image.endsWith(suffix);
      });
    });
  }

  const image: string = (isArray(props.src) && otherImages?.length ? otherImages[0] : props.src) as string;

  return props.src?.length ? (
    <figure
      className={clsx(props.className, {
        'height-auto': props.height === 'auto',
        'width-auto': props.width === 'auto'
      })}
    >
      <picture>
        {avifImages?.length && avifImages[0] ? (
          <source
            srcSet={avifImages[0]}
            type={getImageType(avifImages[0])}
          />
        ) : null}

        {webpImages?.length && webpImages[0] ? (
          <source
            srcSet={webpImages[0]}
            type={getImageType(webpImages[0])}
          />
        ) : null}

        {otherImages?.length && otherImages[0] ? (
          <source
            srcSet={otherImages[0]}
            type={getImageType(otherImages[0])}
          />
        ) : null}

        <img
          alt={props.alt ? props.alt : props.title}
          height={props.height === 'auto' ? undefined : props.height}
          loading={props.loading}
          src={image}
          title={props.title}
          width={props.width === 'auto' ? undefined : props.width}
        />
      </picture>
    </figure>
  ) : null;
};

export { Image };
